import type { StoryVersion, VersionStatus } from '../../api/posts';
import type { BadgeTone } from '../ui';
import { Badge, Button, Card } from '../ui';
import './StoryVersions.css';

export interface StoryVersionsProps {
  versions: StoryVersion[];
  /** Which version is on screen, so the list can say "you are here". */
  current?: number;
  onRead?: (number: number) => void;
  onRestore?: (number: number) => void;
  busy?: number | null;
}

const TONES: Record<VersionStatus, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'accent',
  REJECTED: 'warning',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
};

const LABELS: Record<VersionStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Waiting for review',
  REJECTED: 'Not approved',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
};

function when(version: StoryVersion): string {
  const stamp = version.published_at ?? version.submitted_at ?? version.created_at;
  return new Date(stamp).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Everything this story has said.
 *
 * An archived version is kept rather than deleted, so the list is the record:
 * what was published, when, and what replaced it.
 */
export function StoryVersions({ versions, current, onRead, onRestore, busy }: StoryVersionsProps) {
  return (
    <Card
      title="Versions"
      description="Every version this story has had. A published version is never rewritten — it is replaced."
    >
      <ul className="sc-versions">
        {versions.map((version) => (
          <li key={version.number} className="sc-versions__row">
            <span className="sc-versions__number">
              Version {version.number}
              {version.number === current && (
                <span className="sc-versions__here"> · you are reading this one</span>
              )}
            </span>
            <Badge tone={TONES[version.status]} srPrefix="Status">
              {LABELS[version.status]}
            </Badge>
            <span className="sc-versions__when">
              {version.block_count} block{version.block_count === 1 ? '' : 's'} · {when(version)}
            </span>
            <span className="sc-versions__actions">
              {onRead && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onRead(version.number)}
                  aria-label={`Read version ${version.number}`}
                >
                  Read
                </Button>
              )}
              {/* Only an archived version: what is published is already
                  reachable by starting a draft, and the draft is on screen. */}
              {onRestore && version.status === 'ARCHIVED' && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={busy === version.number}
                  onClick={() => onRestore(version.number)}
                  aria-label={`Bring version ${version.number} back into the draft`}
                >
                  Bring back
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
